import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Bot, Code2, Smartphone, Factory, ExternalLink, Sparkles,
  Check, Calendar, ChevronRight, Shield, Rocket, Lightbulb,
  Wrench, LineChart, Play, X, Layers, Cloud, Database, Cpu,
} from "lucide-react";
import HeroVideo from "@/components/HeroVideo";
import SpaceHero from "@/components/SpaceHero";
import TrustedBy from "@/components/TrustedBy";
import VoiceSocialProof from "@/components/VoiceSocialProof";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
function resolveMediaUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
}

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products)).catch(() => {});
  }, []);

  return (
    <div className="pt-16">
      <Hero />
      <TrustedBy />
      <VoiceSocialProof />
      <TrustedPartner />
      <ServicesSection />
      <WhyChoose />
      <Industries />
      <Process />
      <TechStack />
      <FinalCTA />
    </div>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  return (
    <section className="relative overflow-hidden hero-space" style={{ minHeight: "min(78vh, 700px)" }}>
      {/* Full-bleed background globe video */}
      <SpaceHero />

      {/* Soft right-side mask so text on left stays legible on top of the globe */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, #000 0%, rgba(0,0,0,0.85) 35%, rgba(0,0,0,0.45) 55%, rgba(0,0,0,0) 75%)",
        }}
      />

      {/* Content overlay */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-16 md:pt-14 md:pb-20">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#22D3EE]/30 bg-[#22D3EE]/10 text-[#22D3EE] text-xs uppercase tracking-[0.25em] font-semibold backdrop-blur-sm">
            <Sparkles size={12} /> Web · Mobile · AI · Engineering
          </div>
          <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]"
              style={{ textShadow: "0 4px 24px rgba(0,0,0,0.6)" }}>
            <span className="text-gradient">AI-Driven Software Solutions</span> Built to Scale Your Business
          </h1>
          <p className="mt-6 text-lg text-[#D6D2E1] max-w-xl leading-relaxed" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.7)" }}>
            We help businesses of every size embrace digital transformation through custom software
            development, AI automation, web applications, mobile apps, and intelligent business
            solutions that increase productivity, reduce operational costs, and drive
            <span className="text-white font-medium"> sustainable growth</span>.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a data-testid="hero-start-btn" href="/contact" className="btn-primary">
              Start Your Project <ArrowRight size={16} />
            </a>
            <a data-testid="hero-consult-btn" href="/contact#book" className="btn-secondary backdrop-blur-md">
              <Calendar size={14}/> Book a Free Consultation
            </a>
          </div>
          <div className="mt-4 text-xs text-[#B6B0C7] flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3B82F6] animate-pulse"/>
            <span>Tap the blue mic at the bottom-right to <span className="text-[#3B82F6]">talk to Zero</span> — our voice agent.</span>
          </div>
        </div>
      </div>

    </section>
  );
}

/* ---------- Trusted Partner ---------- */
function TrustedPartner() {
  return (
    <section className="py-20 md:py-24">
      <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
        <div className="eyebrow">About ZeroLabs Digitals</div>
        <h2 className="mt-3 text-3xl md:text-5xl font-bold text-white tracking-tight">
          Your Technology Partner for the Future
        </h2>
        <p className="mt-6 text-[#B6B0C7] leading-relaxed">
          At ZeroLabs Digitals, we combine deep technical expertise with modern technologies to deliver
          high-quality software solutions for startups, SMEs, enterprises and engineering organisations.
        </p>
        <p className="mt-4 text-[#B6B0C7] leading-relaxed">
          Whether you're looking to digitise operations, automate repetitive tasks using Artificial
          Intelligence, develop customer-facing applications, or execute complex engineering design
          projects — our experienced team delivers solutions built for performance, scalability and
          long-term success.
        </p>
        <div className="mt-10 inline-flex items-center gap-3 px-6 py-4 rounded-2xl glow-border">
          <span className="text-xs uppercase tracking-[0.25em] text-[#22D3EE] font-bold">Our mission</span>
          <span className="text-white font-medium">Build innovative technology that creates measurable business value.</span>
        </div>
      </div>
    </section>
  );
}

/* ---------- Services Section ---------- */
const SERVICES = [
  {
    id: "web-app-dev",
    icon: Code2,
    title: "Web Application Development",
    lead: "Transform your business with secure, scalable and high-performance web applications.",
    body: "We build custom enterprise portals, CRM systems, ERP solutions, SaaS platforms, business dashboards, management systems and cloud-based applications designed to streamline operations and improve productivity.",
    bulletTitle: "What We Build",
    items: [
      "Enterprise Web Applications", "CRM & ERP Solutions", "SaaS Platforms",
      "Customer Portals", "Business Dashboards", "Workflow Automation Systems",
      "API Development & Integration", "Cloud Native Applications",
    ],
    cta: { label: "See web work", to: "/software" },
  },
  {
    id: "mobile-app-dev",
    icon: Smartphone,
    title: "Mobile Application Development",
    lead: "Deliver exceptional user experiences through intuitive Android and iOS applications.",
    body: "Our mobile solutions are designed for speed, security and scalability while providing seamless integration with your backend systems.",
    bulletTitle: "Our Expertise",
    items: [
      "Android Applications", "iOS Applications", "Cross-Platform Apps",
      "Enterprise Mobility Solutions", "Customer Engagement Apps",
      "Field Service Applications", "Business Management Apps", "App Maintenance & Support",
    ],
    cta: { label: "See mobile work", to: "/software" },
  },
  {
    id: "ai",
    icon: Bot,
    title: "AI Automation Services",
    lead: "Empower your business with Artificial Intelligence.",
    body: "Artificial Intelligence is transforming how businesses operate. We help organisations automate repetitive tasks, improve customer engagement, reduce operational costs and make smarter decisions using cutting-edge AI technologies.",
    bulletTitle: "AI Solutions We Offer",
    items: [
      "AI Chatbots", "AI Voice Assistants", "AI Customer Support Automation",
      "AI Document Processing", "AI Workflow Automation", "AI Knowledge Assistants",
      "AI-Powered Analytics", "AI Tutor & Learning Platforms",
      "Intelligent Business Process Automation", "Custom AI Solutions",
    ],
    featured: true,
  },
  {
    id: "plant",
    icon: Factory,
    title: "Plant Engineering Design Services",
    lead: "Delivering engineering excellence with precision and industry expertise.",
    body: "Our engineering team supports industrial projects across multiple disciplines, providing high-quality engineering design services that meet international standards.",
    bulletTitle: "Services Include",
    items: [
      "Piping Design", "Plant 3D Design", "E3D Design", "Equipment Modeling",
      "Mechanical Design Support", "Engineering Documentation", "Isometric Drawings",
      "P&ID Support", "CAD Drafting", "Engineering Resource Augmentation",
    ],
    cta: { label: "See engineering work", to: "/plant-engineering" },
  },
];

function ServicesSection() {
  return (
    <section id="services" className="py-20 md:py-24 border-y border-white/5 bg-[#0E0B17]/40">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <div className="eyebrow">Our Services</div>
          <h2 className="mt-2 text-3xl md:text-5xl font-bold text-white tracking-tight">
            End-to-end digital transformation, tailored.
          </h2>
          <p className="mt-4 text-[#B6B0C7]">
            We provide end-to-end digital transformation services tailored to your business requirements.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          {SERVICES.map((s) => <ServiceCard key={s.id} {...s} />)}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ id, icon: Icon, title, lead, body, bulletTitle, items, cta, featured }) {
  return (
    <div className={`card p-7 ${featured ? "glow-border" : ""}`} data-testid={`service-${id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3B82F6]/30 to-[#22D3EE]/30 border border-white/10 text-white flex items-center justify-center">
          <Icon size={22}/>
        </div>
        {featured && (
          <span className="px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/30">Featured</span>
        )}
      </div>
      <h3 className="mt-5 text-2xl font-bold text-white">{title}</h3>
      <p className="mt-2 text-[#D6D2E1] text-[15px] leading-relaxed">{lead}</p>
      <p className="mt-3 text-[#B6B0C7] text-sm leading-relaxed">{body}</p>

      <div className="mt-5 text-xs uppercase tracking-[0.22em] text-[#8C859E] font-semibold">{bulletTitle}</div>
      <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-y-2">
        {items.map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm text-[#D6D2E1]">
            <ChevronRight size={14} className="text-[#22D3EE] mt-0.5 shrink-0"/> {it}
          </li>
        ))}
      </ul>

      {cta && (
        <div className="mt-6">
          <Link to={cta.to} className="text-sm text-[#3B82F6] hover:text-[#22D3EE] inline-flex items-center gap-1">
            {cta.label} <ArrowRight size={14}/>
          </Link>
        </div>
      )}
    </div>
  );
}

/* ---------- Why Choose ---------- */
const WHY = [
  { icon: Wrench, title: "Industry Expertise", text: "Experienced professionals delivering enterprise-grade software and engineering solutions." },
  { icon: Lightbulb, title: "Tailor-Made Solutions", text: "Every solution is designed specifically around your business goals." },
  { icon: Sparkles, title: "Latest Technologies", text: "Leveraging AI, Cloud, modern frameworks, automation and scalable architecture." },
  { icon: Shield, title: "Security First", text: "Building applications with industry-standard security practices and compliance." },
  { icon: Rocket, title: "Agile Development", text: "Transparent communication, rapid iterations and faster delivery." },
  { icon: LineChart, title: "Long-Term Support", text: "Continuous maintenance, upgrades and technical assistance beyond project delivery." },
];

function WhyChoose() {
  return (
    <section id="why" className="py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="eyebrow">Why ZeroLabs Digitals</div>
            <h2 className="mt-3 text-3xl md:text-5xl font-bold text-white tracking-tight">
              Technology that <span className="text-gradient">works</span>. Partnerships that last.
            </h2>
            <p className="mt-5 text-[#B6B0C7]">
              Choosing the right technology partner makes all the difference. Here's why businesses choose us.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {WHY.map(({ icon: Icon, title, text }) => (
              <div key={title} className="card p-5">
                <div className="w-10 h-10 rounded-lg bg-[#22D3EE]/10 text-[#22D3EE] flex items-center justify-center"><Icon size={18}/></div>
                <h4 className="mt-4 text-white font-semibold">{title}</h4>
                <p className="mt-1.5 text-[#B6B0C7] text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Industries ---------- */
const INDUSTRIES = [
  "Education", "Healthcare", "Manufacturing", "Retail & E-Commerce",
  "Finance & Banking", "Logistics", "Engineering & Construction",
  "Hospitality", "Government", "Startups",
];

function Industries() {
  return (
    <section className="py-16 border-y border-white/5 bg-[#0E0B17]/40">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="eyebrow">Industries We Serve</div>
            <h2 className="mt-2 text-2xl md:text-4xl font-bold text-white tracking-tight">Solutions for businesses across diverse industries</h2>
          </div>
          <p className="text-[#B6B0C7] max-w-md">Same engineering rigor, customised for the regulatory, scale and UX needs of each vertical.</p>
        </div>
        <div className="flex flex-wrap gap-2.5">
          {INDUSTRIES.map((i) => (
            <span key={i} className="px-4 py-2 rounded-full text-sm border border-white/10 text-white bg-white/[0.03] hover:border-[#22D3EE]/40 hover:text-[#3B82F6] transition-colors">{i}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Development Process ---------- */
const PROCESS = [
  { n: "01", title: "Discovery", text: "Understanding your business, goals and challenges." },
  { n: "02", title: "Planning", text: "Creating a roadmap with architecture, timelines and milestones." },
  { n: "03", title: "Design", text: "Crafting intuitive user experiences and scalable system designs." },
  { n: "04", title: "Development", text: "Building secure, high-performance applications using best practices." },
  { n: "05", title: "Testing", text: "Ensuring quality through rigorous functional, security and performance testing." },
  { n: "06", title: "Deployment", text: "Smooth deployment with cloud infrastructure and production readiness." },
  { n: "07", title: "Support", text: "Continuous monitoring, maintenance, enhancements and technical support." },
];

function Process() {
  return (
    <section className="py-20 md:py-24 relative">
      <div className="absolute inset-0 bg-[radial-gradient(800px_400px_at_50%_0,rgba(34,211,238,0.10),transparent_60%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="text-center max-w-3xl mx-auto">
          <div className="eyebrow">Our Development Process</div>
          <h2 className="mt-2 text-3xl md:text-5xl font-bold text-white tracking-tight">How we work</h2>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PROCESS.map((p) => (
            <div key={p.n} className="card p-6 relative">
              <span className="font-mono text-xs text-[#3B82F6]">{p.n}</span>
              <h4 className="mt-3 text-lg font-semibold text-white">{p.title}</h4>
              <p className="mt-2 text-sm text-[#B6B0C7] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Tech Stack ---------- */
const STACK = [
  { icon: Layers, title: "Backend", items: ["Java", "Spring Boot", "Node.js", "Python", "REST APIs", "Microservices"] },
  { icon: Code2, title: "Frontend", items: ["React", "Angular", "Next.js", "HTML5", "CSS3", "JavaScript", "TypeScript"] },
  { icon: Smartphone, title: "Mobile", items: ["Flutter", "React Native", "Android", "iOS"] },
  { icon: Cloud, title: "Cloud & DevOps", items: ["AWS", "Docker", "Kubernetes", "CI/CD", "GitHub", "GitLab"] },
  { icon: Database, title: "Database", items: ["PostgreSQL", "MySQL", "MongoDB", "SQL Server", "Redis"] },
  { icon: Cpu, title: "AI", items: ["OpenAI", "Gemini", "Anthropic Claude", "LangChain", "Vector Databases", "RAG", "AI Agents", "Speech AI", "Voice Automation"] },
];

function TechStack() {
  return (
    <section className="py-20 md:py-24 border-y border-white/5 bg-[#0E0B17]/40">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <div className="eyebrow">Technologies We Use</div>
          <h2 className="mt-2 text-3xl md:text-5xl font-bold text-white tracking-tight">Modern, enterprise-grade platforms</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {STACK.map(({ icon: Icon, title, items }) => (
            <div key={title} className="card p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/10 text-[#3B82F6] flex items-center justify-center"><Icon size={18}/></div>
                <h4 className="text-white font-semibold">{title}</h4>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {items.map((it) => (
                  <span key={it} className="px-3 py-1.5 rounded-full text-xs border border-white/10 text-[#D6D2E1] bg-white/[0.03] hover:border-[#22D3EE]/40 transition-colors">{it}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Products ---------- */
function Products({ products }) {
  const [activeVideo, setActiveVideo] = useState(null);
  if (!products || products.length === 0) return null;

  return (
    <section id="products" className="py-24 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
          <div>
            <div className="eyebrow">Products</div>
            <h2 className="mt-2 text-3xl md:text-5xl font-bold text-white tracking-tight">Software we built, ready to use</h2>
          </div>
          <p className="text-[#B6B0C7] max-w-md">Live products and what's coming next. Ask Zero "what products do you offer?" — get the list with demo links.</p>
        </div>

        <div className="space-y-16">
          {products.map((p, idx) => {
            const reverse = idx % 2 === 1;
            const live = p.tag === "Live";
            const host = p.url && p.url !== "#" ? new URL(p.url).hostname : "coming-soon.zerolabs.digital";
            const videoUrl = resolveMediaUrl(p.demo_video_url);
            return (
              <div key={p.id} className={`grid lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`product-${p.id}`}>
                <div className="lg:col-span-6 relative">
                  <div className="absolute -inset-4 bg-[radial-gradient(circle_at_30%_30%,rgba(59,130,246,0.18),transparent_60%)] blur-2xl"/>
                  <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
                    {videoUrl ? (
                      <video src={videoUrl} autoPlay muted loop playsInline className="w-full h-[360px] object-cover" data-testid={`product-${p.id}-video-preview`}/>
                    ) : (
                      <img src={p.image} alt={p.name} className="w-full h-[360px] object-cover"/>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#08060F] via-transparent to-transparent pointer-events-none"/>
                    {videoUrl && (
                      <button data-testid={`product-${p.id}-play-overlay`} onClick={() => setActiveVideo({ name: p.name, url: videoUrl })}
                        className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors" aria-label={`Play ${p.name} demo`}>
                        <span className="w-20 h-20 rounded-full bg-[#3B82F6] text-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                          <Play size={32} fill="currentColor"/>
                        </span>
                      </button>
                    )}
                    <div className="absolute top-4 left-4 pointer-events-none">
                      <span className={`px-2 py-1 rounded-md text-[10px] uppercase tracking-[0.2em] border ${live ? "bg-[#22D3EE]/15 text-[#22D3EE] border-[#22D3EE]/40" : "bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/40"}`}>{p.tag}</span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between pointer-events-none">
                      <span className="px-2 py-1 rounded-md bg-[#08060F]/80 border border-white/10 text-xs font-mono text-[#22D3EE]">{host}</span>
                      {live ? (
                        <a href={p.url} target="_blank" rel="noreferrer" className="text-xs text-white inline-flex items-center gap-1 hover:text-[#22D3EE] pointer-events-auto">Visit <ExternalLink size={12}/></a>
                      ) : (
                        <span className="text-xs text-[#B6B0C7]">Demo video coming soon</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-6">
                  <div className="eyebrow">{p.tagline}</div>
                  <h3 className="mt-2 text-3xl md:text-4xl font-bold text-white">{p.name}</h3>
                  <p className="mt-4 text-[#B6B0C7] leading-relaxed">{p.description}</p>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-3">
                    {p.highlights.map((h) => (
                      <li key={h} className="flex items-center gap-2 text-sm text-[#D6D2E1]">
                        <Check size={14} className="text-[#3B82F6]"/> {h}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex flex-wrap gap-3">
                    {live ? (
                      <a href={p.url} target="_blank" rel="noreferrer" className="btn-primary" data-testid={`product-${p.id}-launch`}>
                        Launch {p.name} <ExternalLink size={14}/>
                      </a>
                    ) : (
                      <button disabled className="btn-primary opacity-60 cursor-not-allowed" data-testid={`product-${p.id}-launch`}>
                        Notify me at launch
                      </button>
                    )}
                    {videoUrl ? (
                      <button onClick={() => setActiveVideo({ name: p.name, url: videoUrl })} className="btn-secondary" data-testid={`product-${p.id}-watch-demo`}>
                        <Play size={14}/> Watch demo
                      </button>
                    ) : (
                      <a href="/contact#book" className="btn-secondary">Request a demo</a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {activeVideo && <VideoLightbox video={activeVideo} onClose={() => setActiveVideo(null)} />}
    </section>
  );
}

function VideoLightbox({ video, onClose }) {
  return (
    <div data-testid="video-lightbox" role="dialog" aria-modal="true" onClick={onClose}
      className="fixed inset-0 z-[70] flex items-center justify-center p-6"
      style={{ background: "rgba(8, 6, 15, 0.88)", backdropFilter: "blur(8px)" }}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl">
        <div className="flex items-center justify-between mb-3">
          <div className="text-white font-semibold">{video.name} — demo</div>
          <button data-testid="video-lightbox-close" onClick={onClose} className="text-[#B6B0C7] hover:text-white p-1"><X/></button>
        </div>
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-black">
          <video src={video.url} controls autoPlay playsInline className="w-full h-auto max-h-[78vh] block" data-testid="video-lightbox-player"/>
        </div>
      </div>
    </div>
  );
}

/* ---------- Final CTA ---------- */
function FinalCTA() {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="orb orb-orange" style={{ top: "30%", left: "10%", width: "320px", height: "320px", opacity: 0.35 }} />
      <div className="orb orb-purple" style={{ top: "10%", right: "10%", width: "360px", height: "360px", opacity: 0.45 }} />
      <div className="max-w-5xl mx-auto px-6 md:px-10 relative text-center">
        <div className="eyebrow">Ready when you are</div>
        <h2 className="mt-3 text-3xl md:text-6xl font-extrabold tracking-tighter text-white leading-[1.05]">
          Ready to Build Your Next <span className="text-gradient">Digital Solution?</span>
        </h2>
        <p className="mt-5 text-[#B6B0C7] max-w-2xl mx-auto leading-relaxed">
          Whether you're launching a startup, modernising enterprise systems, automating business
          processes with AI, or looking for engineering design expertise — ZeroLabs Digitals is ready
          to help. Let's discuss your project and turn your vision into reality.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a href="/contact#book" className="btn-primary" data-testid="final-cta-book"><Calendar size={16}/> Book a Free Consultation</a>
          <a href="/contact" className="btn-secondary" data-testid="final-cta-proposal">Request a Proposal <ArrowRight size={14}/></a>
        </div>
      </div>
    </section>
  );
}

